import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import JobList from "./JobList";
import theme from "../../../components/LayoutComponents/theme";
import { getJobsRequest } from "../../../remote/jobRequests";

const JobBoard = ({ session, userComp, city, state, schools, isMobile, programs, courses, userDetails }) => {
  const [filters, setFilters] = useState({
    title: "",
    company: "",
    country: "United States",
    state: "",
    city: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // 🧠 Fetch jobs when filters or page change
  useEffect(() => {
    if (!session?.userId) return;

    const fetchJobs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          userId: session.userId,
          city: appliedFilters.city,   // use empty string if cleared
          state: appliedFilters.state, // use empty string if cleared
          title: appliedFilters.title || "",
          company: appliedFilters.company || "",
          page: page.toString()
        });

        const res = await getJobsRequest(session.token, params);

        if (!res || !res.data) throw new Error("Invalid response from backend");
        const { jobs: fetchedJobs, totalCount: total } = res.data;

        setJobs(fetchedJobs || []);
        setTotalCount(total || 0);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [appliedFilters, page, session, city, state]);

  const mappedJobs = useMemo(
    () => [
      ...(Array.isArray(jobs)
        ? jobs.map((job) => ({
            jobPostingId: job.jobPostingId,
            title: job.jobPostingName,
            company: job.company,
            location: job.location,
            country: job.country,
            state: job.state,
            city: job.city,
            datePosted: new Date(Date.parse(job.jobOpenPostingDate))
              .toISOString()
              .split("T")[0],
            companyLink: job.linkApply,
            isFeatured: job.isFeatured,
            dutiesResponsibilities: job.dutiesResponsibilities,
            requiredEducation: job.requiredEducation,
            workplacePolicy: job.workplacePolicy,
            match: job.matchPercentage,
            logo: job.logoUrl,
          }))
        : []),
    ],
    [jobs]
  );



  // 🧭 Use combinedJobs for filtering and sorting
  const sortedJobs = useMemo(() => {
    if (!mappedJobs) return [];
    return mappedJobs.slice().sort((a, b) => {
      const aMatchNum = !isNaN(Number(a.match)) ? Number(a.match) : null;
      const bMatchNum = !isNaN(Number(b.match)) ? Number(b.match) : null;
      const aIsNA = a.match === "N/A" || aMatchNum === null;
      const bIsNA = b.match === "N/A" || bMatchNum === null;
      if (aIsNA && !bIsNA) return 1;
      if (!aIsNA && bIsNA) return -1;
      if (aIsNA && bIsNA) return 0;
      return bMatchNum - aMatchNum;
    });
  }, [mappedJobs]);

  // 🔁 Filter handlers
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    const reset = { title: "", company: "", country: "", state: "", city: "" };
    setFilters(reset);
    setAppliedFilters(reset);
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / 1000);


  // 🎛️ Filter fields UI
  const FilterFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          label="Job Title"
          variant="outlined"
          value={filters.title}
          onChange={(e) => handleFilterChange("title", e.target.value)}
          size={isMobile ? "small" : "medium"}
          helperText="Filter by job title."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          label="Company"
          variant="outlined"
          value={filters.company}
          onChange={(e) => handleFilterChange("company", e.target.value)}
          size={isMobile ? "small" : "medium"}
          helperText="Filter by company name."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          label="State"
          variant="outlined"
          value={filters.state}
          onChange={(e) => handleFilterChange("state", e.target.value)}
          size={isMobile ? "small" : "medium"}
          helperText="Filter by state."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          label="City"
          variant="outlined"
          value={filters.city}
          onChange={(e) => handleFilterChange("city", e.target.value)}
          size={isMobile ? "small" : "medium"}
          helperText="Filter by city."
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        <PurpleButton label="Apply Filters" onClick={handleApplyFilters} />
        <PurpleButton label="Clear Filters" onClick={handleClearFilters} />
      </Grid>
    </Grid>
  );

  // 🖥️ Render
  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ color: theme.colors.purple, fontWeight: "bold", marginBottom: "1rem" }}
      >
        Job Board
      </Typography>

      {/* Filters */}
      {isMobile ? (
        <Accordion sx={{ width: "100%", maxWidth: "1200px", mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{FilterFields}</AccordionDetails>
        </Accordion>
      ) : (
        <Box
          sx={{
            position: "sticky",
            top: "6%",
            zIndex: 100,
            background: "#fff",
            paddingTop: "1rem",
            paddingBottom: "1rem",
            marginBottom: "1rem",
            width: "100%",
            maxWidth: "1200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {FilterFields}
        </Box>
      )}

      {/* Jobs or Loader */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", width: "100%" }}>
          <LoadingCircle />
        </Box>
      ) : (
        <>
          <JobList
            session={session}
            jobs={sortedJobs}
            totalCount={totalCount}
            userComp={userComp}
            city={city}
            state={state}
            isMobile={isMobile}
            schools={schools}
            programs={programs}
            courses={courses}
          />

          {/* Pagination Control */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default JobBoard;
